'use client';

import { useEffect, useState } from 'react';
import { HStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import type {
  CopilotMode,
  CopilotModeSwitchMessage,
} from '@terra/shared-dto-chat-types';
import { BodyDefault, MetaDefault } from '../../../components/typography';
import { FlatToolCardActionButton } from './tools/shared/flat-tool-card-action-button';
import { FlatToolCard } from './tools/shared/flat-tool-card';

export type OnRevertCopilotModeSwitch = (data: { toMode: CopilotMode }) => void;

export function CopilotModeSwitchCard({
  dataMessage,
  onRevert,
  onConfirm,
  currentMode,
}: {
  dataMessage?: CopilotModeSwitchMessage;
  onRevert?: OnRevertCopilotModeSwitch;
  onConfirm?: OnRevertCopilotModeSwitch;
  currentMode?: CopilotMode;
}) {
  const t = useTranslations('designSystem.chat.copilotModeSwitchCard');
  const [hasBeenApplied, setHasBeenApplied] = useState(
    currentMode === dataMessage?.toMode,
  );

  useEffect(() => {
    if (dataMessage && currentMode === dataMessage.toMode) {
      setHasBeenApplied(true);
    }
  }, [currentMode, dataMessage]);

  if (!dataMessage) {
    return null;
  }

  const modeLabel = (mode: CopilotMode): string => {
    if (mode === 'agent') {
      return t('modeAgent');
    }

    if (mode === 'plan') {
      return t('modePlan');
    }

    return t('modeAsk');
  };

  const isPending =
    Boolean(dataMessage.requiresConfirmation) && !hasBeenApplied;
  const canRevert =
    !isPending && currentMode === dataMessage.toMode && Boolean(onRevert);
  const targetModeLabel = modeLabel(dataMessage.toMode);

  return (
    <FlatToolCard
      title={
        isPending
          ? t('confirmTitle', { mode: targetModeLabel })
          : t('switchedTo', { mode: targetModeLabel })
      }
      tone={dataMessage.toMode}
      headerActions={
        isPending && onConfirm ? (
          <HStack gap={0.5} alignItems="center">
            <FlatToolCardActionButton
              onClick={() => {
                setHasBeenApplied(true);
                onConfirm({ toMode: dataMessage.toMode });
              }}
            >
              {t('confirmAccept', { mode: targetModeLabel })}
            </FlatToolCardActionButton>
          </HStack>
        ) : canRevert ? (
          <HStack gap={0.5} alignItems="center">
            <FlatToolCardActionButton
              onClick={() => onRevert?.({ toMode: dataMessage.fromMode })}
            >
              {t('revert')}
            </FlatToolCardActionButton>
          </HStack>
        ) : undefined
      }
    >
      {isPending && dataMessage.reason ? (
        <BodyDefault color="fg.muted">
          {t('confirmDescription', { reason: dataMessage.reason })}
        </BodyDefault>
      ) : null}
      {!isPending && dataMessage.reason ? (
        <MetaDefault color="fg.muted">{dataMessage.reason}</MetaDefault>
      ) : null}
    </FlatToolCard>
  );
}
